import Config from 'config';
let config;
import mediator, { ACTIONS } from 'mediator';
import fastdom from 'fastdom';
import { find, has, includes } from 'lodash';
import svgClasses from 'lib/common/svgClasses';
import renderCustomIcon from 'lib/seats/renderCustomIcon';
import { getSeatType, getAvailableSeatTypes } from 'lib/seats/seatTypes';
import { getColorCode } from 'lib/common/colorCode';

const CLASS_NAMES = {
  base: 'seat',
  selected: 'seat--selected',
  hidden: 'seat--hidden',
  killed: 'seat--killed'
};

function getClassNames(data, prices) {
  config = new Config().getConfig();
  let classNames = [CLASS_NAMES.base];
  let statusCode = data.seat_status.status_code;

  // We want to artificially mark seat as unavailable if there is no
  // pricing information for it
  let priceExists = prices.some(price => {
    return price.zone_no === data.zone_no;
  });

  if (!priceExists) {
    statusCode = 'TKD';
  }

  let seatType = getSeatType(statusCode);

  classNames.push(`seat--${seatType.slug}`);

  /**
   * Seats with specific status or hold codes should be hidden
   */
  if (
    includes(config.seatTypes.hiddenStatusCodes, data.seat_status.status_code) ||
    includes(config.seatTypes.hiddenHoldCodes, data.hc_no)
  ) {
    classNames.push(CLASS_NAMES.killed);
  }

  /**
   * Apply status class based on seat type id
   */
  let seatTypes = getAvailableSeatTypes();
  seatTypes.forEach(function(type) {
    if (
      has(type, 'seatTypeIds') &&
      includes(type.seatTypeIds, data.seat_type)
    ) {
      classNames.push(`seat--${seatType.slug}`);
    }
  });

  return classNames;
}

function clickHandler(el, data) {
  el.addEventListener('click', function() {
    mediator.publish(ACTIONS.SEAT_SELECTED, { seatEl: el, seatData: data });
  });
}

function injectCustomIconElement(type, seat, classNames, colours) {
  let newEl;
  try {
    newEl = renderCustomIcon(type.customName, seat.el, classNames, getColorCode(seat.data, colours));
  } catch (e) {} // eslint-disable-line no-empty

  if (newEl) {
    // Set an id on the new seat icon for later retrieval.
    newEl.id = `syos-custom-icon-${seat.data.seat_no}`;

    // Hide the old element
    svgClasses.addClass(seat.el, CLASS_NAMES.killed);

    // Re-add the click handler to the new element
    clickHandler(newEl, seat.data);

    return newEl;
  } else {
    return seat.el;
  }
}

function enhanceSeatEls(seats, colours, prices) {
  let customSeatTypes = getAvailableSeatTypes().filter(function(x) {
    return x.method === 'custom';
  });

  seats.map(function(seat) {
    if (seat.data && seat.el) {
      let colourCode = getColorCode(seat.data, colours);
      let classNames = getClassNames(seat.data, prices);

      classNames.forEach(function(name) {
        svgClasses.addClass(seat.el, name);

        if (seat.data.seat_status.description === 'Available') {
          if (colourCode) {
            seat.el.setAttribute('style', `stroke: ${colourCode}`);
          }
        }
      });

      clickHandler(seat.el, seat.data);

      let matchingIconForHoldCode = find(customSeatTypes, function(type) {
        return (
          has(type, 'customName') &&
          has(type, 'holdCodes') &&
          includes(type.holdCodes, seat.data.hc_no)
        );
      });

      if (matchingIconForHoldCode) {
        if (colourCode) {
          seat.el = injectCustomIconElement(matchingIconForHoldCode, seat, classNames, colours);
        } else {
          seat.el = injectCustomIconElement(matchingIconForHoldCode, seat, classNames, false);
        }
      }

      let matchingIconForSeatType = find(customSeatTypes, function(type) {
        return (
          has(type, 'customName') &&
          has(type, 'seatTypeIds') &&
          includes(type.seatTypeIds, seat.data.seat_type)
        );
      });

      if (matchingIconForSeatType) {
        if (colourCode) {
          seat.el = injectCustomIconElement(matchingIconForSeatType, seat, classNames, colours);
        } else {
          seat.el = injectCustomIconElement(matchingIconForSeatType, seat, classNames, false);
        }
      }
    }

    return seat;
  });

  return seats;
}

function priceFilters(seats) {
  mediator.subscribe(ACTIONS.PRICE_FILTER_SELECTED, function(payload) {
    const selected = payload.selected;

    if (selected.length) {
      const otherSeats = seats.filter(seat => {
        return has(seat, 'data.zone_no') ? selected.indexOf(seat.data.zone_no) === -1 : false;
      });

      fastdom.mutate(function() {
        seats.forEach(function(seat) {
          svgClasses.removeClass(seat.el, CLASS_NAMES.hidden);
        });

        otherSeats.forEach(function(seat) {
          svgClasses.addClass(seat.el, CLASS_NAMES.hidden);
        });
      });
    } else {
      fastdom.mutate(function() {
        seats.forEach(function(seat) {
          svgClasses.removeClass(seat.el, CLASS_NAMES.hidden);
        });
      });
    }
  });
}

/**
 * Toggle seat selection style
 * Need to a bit verbose with checks to avoid cases where
 * multiple events are triggered and cause double toggling
 */
function toggleSelected(seatEl, isSelected) {
  if (isSelected && !svgClasses.hasClass(seatEl, CLASS_NAMES.selected)) {
    svgClasses.addClass(seatEl, CLASS_NAMES.selected);
  }

  if (!isSelected && svgClasses.hasClass(seatEl, CLASS_NAMES.selected)) {
    svgClasses.removeClass(seatEl, CLASS_NAMES.selected);
  }
}

function selectionListeners() {
  mediator.subscribe(ACTIONS.SEAT_ADDED, function(seat) {
    seat && toggleSelected(seat.seatEl, true);
  });
  mediator.subscribe(ACTIONS.SEAT_TOGGLE_SELECTED, function(seat) {
    seat && toggleSelected(seat.seatEl, true);
  });
  mediator.subscribe(ACTIONS.SEAT_DESELECTED, function(seat) {
    seat && toggleSelected(seat.seatEl, false);
  });
}

function enhanceWithZoneGrouping(seats, prices) {
  seats.forEach(seat => {
    prices.forEach(price => {
        if (price.zone_no === seat.data.zone_no) {
          seat.data.zone_group_no = price.zone_group_no;
        }
    });
  });

  return seats;
}

export default function(seats, colours, prices) {
  seats = enhanceWithZoneGrouping(seats, prices);

  let enhancedSeats = enhanceSeatEls(seats, colours, prices);
  priceFilters(seats);
  selectionListeners();

  return enhancedSeats;
}



// WEBPACK FOOTER //
// ./src/javascripts/behaviours/enhanceSeats.js